import { ENDPOINT } from './../../constants/api';
import { ResponseDTO } from 'src/dto/base.dto';
import { makeAutoObservable, observable, runInAction, computed, action } from 'mobx';
import { ONBOARDING_CURRENCY_TYPE } from 'src/constants';
import {
	ILoanDetails,
	IMortgageDetailsForm,
	IMortgageDetailsFormRequest,
	IReferral,
	ISecurityDetails
} from 'src/interfaces/mortgage-deal/mortgage-deal-details';
import { IHttpService } from 'src/services/http.service';
import {
	DeleteGuarantorInvitationDTO,
	DeleteMortgageSupportingDocumentDTO,
	GetMortgageDealDetailsDTO,
	ResendEmailGuarantorDTO,
	SaveDraftMortgageDealDTO,
	SaveGuarantorDTO,
	saveMortgageDealDetailsDTO,
	SubmitMortgageDealFormDTO
} from 'src/dto/mortgage-deal-details.dto';
import { HTTP_STATUS_RESPONSE_KEY } from 'src/constants/api';
import { IFileGuarantor, IGuarantor, IGuarantorRequest } from 'src/interfaces/mortgage-deal/invite-guarantor';
import { IMortgageSupportingDoc } from 'src/interfaces/mortgage-deal/supporting-document';
import { IDocument } from 'src/interfaces/accounts/account';
import { GuaranteeTypeEnum } from 'src/constants/guarantee';
import { convertStringToNumberForObject, convertNumberToStringForObject } from 'src/helpers/common.utils';

export interface IMortgageDealDetails {
	mortgageDealForm: IMortgageDetailsForm;

	supportingDocuments: IMortgageSupportingDoc;

	guarantors: IGuarantor[];

	get mainReferral(): IReferral;

	get referralList(): IReferral[];

	get mainGuarantor(): IGuarantor;

	get memberHasConsentedInGuarantorList(): boolean;

	get allGuarantorHasConsented(): boolean;

	saveMortgageDealDetails(body: IMortgageDetailsFormRequest): Promise<boolean>;

	getMortgageDealDetails(id?: string): Promise<void>;

	uploadFileMainGuarantor(form: FormData, idx: number): Promise<IFileGuarantor>;

	saveGuarantor(guarantorsList: IGuarantor[], inviteGuarantor: boolean): Promise<boolean>;

	removeGuarantorInvitation(index: number): Promise<boolean>;

	deleteFile(id?: string): Promise<boolean>;

	uploadFile(form: FormData): Promise<boolean>;

	downloadFile(type: keyof IMortgageSupportingDoc, id?: string): Promise<void>;

	saveDraft(): Promise<boolean>;

	submitMortgageDealForm(): Promise<boolean>;

	dispose(): void;

	resendEmailGuarantor(guarantor: IGuarantor): Promise<boolean>;

	updateReferralQuestion(value: string): void;

	get canSubmitMortgageDealForm(): boolean;

	updateUseOfFunds(value: string): void;

	clearUseOfFundOther(): void;

	updateOwnership(value: string): void;

	clearOtherOwnership(): void;

	updateCountrySecurityDetails(value: string): void;

	updateCountrySolicitorDetails(value: string): void;

	updateCountryPropertyCompanyManagement(value: string): void;

	updateMortgageApprovedBySecondBankQuestion(value?: boolean): void;

	updateIsSecondMortgageForSecurityQuestion(value?: boolean): void;

	clearMortgageApprovedBySecondBankQuestionNote(): void;

	clearIsSecondMortgageForSecurityQuestionNote(): void;

}

export class mortgageDealDetailsStore implements IMortgageDealDetails {
	mortgageDealForm: IMortgageDetailsForm = {
		loanDetails: {
			borrowCurrency: ONBOARDING_CURRENCY_TYPE.HKD
		},
		securityDetails: {
			purchaseCurrency: ONBOARDING_CURRENCY_TYPE.HKD,
			currentOrExpectedCurrency: ONBOARDING_CURRENCY_TYPE.HKD,
			groundRentPerMonthCurrency: ONBOARDING_CURRENCY_TYPE.HKD,
			buildingManagementFeePerMonthCurrency: ONBOARDING_CURRENCY_TYPE.HKD
		},
		doYouHaveAReferral: undefined
	};

	guarantors: IGuarantor[] = [];

	supportingDocuments: IMortgageSupportingDoc = {};

	constructor(protected readonly http: IHttpService) {
		makeAutoObservable(this, {
			supportingDocuments: observable.deep,
			mortgageDealForm: observable.deep,
			mainReferral: computed,
			referralList: computed,
			mainGuarantor: computed,
			removeGuarantorInvitation: action.bound,
			memberHasConsentedInGuarantorList: computed,
			allGuarantorHasConsented: computed,
			updateReferralQuestion: action.bound,
			canSubmitMortgageDealForm: computed,
			updateUseOfFunds: action.bound,
			clearUseOfFundOther: action.bound,
			updateOwnership: action.bound,
			clearOtherOwnership: action.bound,
			updateCountrySecurityDetails: action.bound,
			updateCountrySolicitorDetails: action.bound,
			updateCountryPropertyCompanyManagement: action.bound,
			updateMortgageApprovedBySecondBankQuestion: action.bound,
			updateIsSecondMortgageForSecurityQuestion: action.bound,
			clearIsSecondMortgageForSecurityQuestionNote: action.bound,
			clearMortgageApprovedBySecondBankQuestionNote: action.bound
		});
	}
	
	get allGuarantorHasConsented(): boolean {
		const guarantorList = this.guarantors.slice();

		const isContested =
			guarantorList.length > 0 && guarantorList.every((guarantor) => guarantor.status === GuaranteeTypeEnum.Consented);

		return isContested;
	}

	get memberHasConsentedInGuarantorList(): boolean {
		const guarantorList = this.guarantors.slice();

		const isContested = guarantorList.some((guarantor) => guarantor.status === GuaranteeTypeEnum.Consented);

		return isContested;
	}

	get mainGuarantor(): IGuarantor {
		if (!this.guarantors) {
			return {};
		}
		return this.guarantors[0];
	}

	get mainReferral(): IReferral {
		if (!this.mortgageDealForm?.thirdPartyReferral) {
			return {};
		}
		return this.mortgageDealForm.thirdPartyReferral[0];
	}

	get referralList(): IReferral[] {
		if (!this.mortgageDealForm?.thirdPartyReferral) {
			return [];
		}
		return this.mortgageDealForm.thirdPartyReferral.slice(1) || [];
	}

	public async saveMortgageDealDetails(
		body: IMortgageDetailsFormRequest & { mainReferral: IReferral }
	): Promise<boolean> {
		body.loanDetails = convertStringToNumberForObject<ILoanDetails>(body.loanDetails, ['borrowAmount']);

		body.securityDetails = convertStringToNumberForObject<ISecurityDetails>(body.securityDetails, [
			'buildingManagementFeePerMonth',
			'currentOrExpectedRentPerMonth',
			'groundRentPerMonth',
			'purchasePrice'
		]);

		const requestBody: IMortgageDetailsForm = {
			id: this.mortgageDealForm?.id,
			loanDetails: body.loanDetails,
			securityDetails: body.securityDetails,
			solicitorDetails: body.solicitorDetails,
			propertyCompanyManagement: body.propertyCompanyManagement,
			thirdPartyReferral: body.mainReferral ? [body.mainReferral] : undefined,
			isCorporateRequest: body.isCorporateRequest
		};

		requestBody.securityDetails = {
			...body.securityDetails,
			districtName: body.securityDetails.district
		};

		if (body.thirdPartyReferral?.referralList) {
			requestBody.thirdPartyReferral = [body.mainReferral, ...body.thirdPartyReferral.referralList];
		}

		const dto = new saveMortgageDealDetailsDTO(requestBody);

		const rs: ResponseDTO<IMortgageDetailsForm> = await this.http.request<
			saveMortgageDealDetailsDTO,
			IMortgageDetailsForm
		>(dto);

		if (rs.responseKey === HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				this.mortgageDealForm = {
					...rs.data,
					loanDetails: {
						...convertNumberToStringForObject<ILoanDetails>(body.loanDetails, ['borrowAmount'])
					},
					securityDetails: {
						...convertNumberToStringForObject<ISecurityDetails>(body.securityDetails, [
							'buildingManagementFeePerMonth',
							'currentOrExpectedRentPerMonth',
							'groundRentPerMonth',
							'purchasePrice'
						])
					},
					doYouHaveAReferral: body.doYouHaveAReferral
				};
			});
			return true;
		}
		return false;
	}

	public async getMortgageDealDetails(id: string) {
		const dto = new GetMortgageDealDetailsDTO({ id });

		const rs: ResponseDTO<IMortgageDetailsForm> = await this.http.request<
			GetMortgageDealDetailsDTO,
			IMortgageDetailsForm
		>(dto);

		if (rs.responseKey === HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				this.mortgageDealForm = {
					...rs.data,
					loanDetails: {
						...convertNumberToStringForObject<ILoanDetails>(rs.data?.loanDetails || {}, ['borrowAmount'])
					},
					securityDetails: {
						...convertNumberToStringForObject<ISecurityDetails>(rs.data?.securityDetails || {}, [
							'buildingManagementFeePerMonth',
							'currentOrExpectedRentPerMonth',
							'groundRentPerMonth',
							'purchasePrice'
						])
					},
					doYouHaveAReferral: rs.data && rs.data.thirdPartyReferral ? 'yes' : 'no'
				};

				if (rs.data && rs.data.mortgageGuarantors) {
					this.guarantors = rs.data.mortgageGuarantors;
				}

				if (rs.data && rs.data.supportingDocuments) {
					this.supportingDocuments = rs.data.supportingDocuments;
				}
			});
		}
	}

	public async uploadFileMainGuarantor(form: FormData, idx: number): Promise<IFileGuarantor> {
		form.append('mortgageId', this.mortgageDealForm?.id as string);

		form.append('fileIndex', idx.toString());

		const rs: ResponseDTO<IFileGuarantor> = await this.http.uploadFile(ENDPOINT.UPLOAD_FILE_INVITE_GUARANTOR, form);

		if (rs.responseKey === HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				const currentList = this.guarantors.slice();

				currentList[idx] = {
					...currentList[idx],
					document: rs.data,
					documentId: rs.data?.id
				};

				this.guarantors = currentList;
			});
			return rs.data || {};
		}

		return {};
	}

	public async saveGuarantor(guarantorsList: IGuarantor[], inviteGuarantor: boolean): Promise<boolean> {
		let requestGuarantorList: IGuarantor[] = [];
		if (guarantorsList && guarantorsList.length) {
			requestGuarantorList = guarantorsList.map((e, idx) => {
				return {
					...this.guarantors[idx],
					...e,
					documentId: this.guarantors[idx].documentId
				};
			});
		}

		const requestBody: IGuarantorRequest = {
			mortgageApplicationId: this.mortgageDealForm.id || '',
			guarantors: inviteGuarantor ? [...requestGuarantorList] : [],
			inviteGuarantor
		};

		const dto = new SaveGuarantorDTO(requestBody);

		const rs: ResponseDTO<IGuarantor[]> = await this.http.request<SaveGuarantorDTO, IGuarantor[]>(dto);

		if (rs.responseKey === HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				if (rs.data) {
					this.guarantors = rs.data;
				}
			});

			return true;
		}

		return false;
	}

	public async removeGuarantorInvitation(index: number): Promise<boolean> {
		const cloned = this.guarantors.slice(0);
		const data = cloned[index];
		if (data) {
			if (data.id) {
				const dto = new DeleteGuarantorInvitationDTO({ id: data.id });
				const rs: ResponseDTO<boolean> = await this.http.request<DeleteGuarantorInvitationDTO, boolean>(dto);
				if (rs.responseKey === HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
					runInAction(() => {
						cloned.splice(index, 1);
						this.guarantors = [...cloned];
					});
					return true;
				}
				return false;
			} else {
				cloned.splice(index, 1);
				this.guarantors = [...cloned];
				return true;
			}
		}

		return true;
	}

	public async deleteFile(id?: string): Promise<boolean> {
		if (!id) {
			return true;
		}
		const dto = new DeleteMortgageSupportingDocumentDTO({
			documentId: id
		});

		const res: ResponseDTO<IMortgageSupportingDoc> = await this.http.request<
			DeleteMortgageSupportingDocumentDTO,
			IMortgageSupportingDoc
		>(dto);
		if (res.responseKey === HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				this.supportingDocuments = {
					...res.data
				};
			});
			return true;
		}
		return false;
	}

	async uploadFile(form: FormData): Promise<boolean> {
		form.append('mortgageId', this.mortgageDealForm.id || '');

		const res: ResponseDTO<IMortgageSupportingDoc> = await this.http.uploadFile(
			ENDPOINT.UPLOAD_MORTGAGE_APPLICATION_DOC,
			form
		);
		if (res.responseKey === HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				this.supportingDocuments = {
					...res.data
				};
			});
			return true;
		}
		return false;
	}

	public async downloadFile(type: keyof IMortgageSupportingDoc, id?: string): Promise<void> {
		if (!id || !this.supportingDocuments || !this.supportingDocuments[type]) {
			return;
		}
		const documents: IDocument | IDocument[] | undefined = this.supportingDocuments[type];
		let doc: IDocument | undefined;

		if (!documents) {
			return;
		}
		if (Array.isArray(documents)) {
			doc = documents.find((d) => d.id == id);
		} else {
			doc = documents;
		}
		if (!doc) {
			return;
		}
		await this.http.downloadFile(
			ENDPOINT.DOWNLOAD_MORTGAGE_DOC_FILE.concat(id),
			{},
			{
				name: doc.fileName || ''
			}
		);
	}

	async saveDraft(): Promise<boolean> {
		const dto = new SaveDraftMortgageDealDTO({ mortgageId: this.mortgageDealForm.id || '' });
		const result: ResponseDTO<string> = await this.http.request<SaveDraftMortgageDealDTO, string>(dto);
		if (result.responseKey == HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				this.dispose();
			});
			return true;
		}
		return false;
	}

	public async submitMortgageDealForm(): Promise<boolean> {
		const mortgageId = this.mortgageDealForm.id || '';
		const dto = new SubmitMortgageDealFormDTO({ mortgageId });

		const rs: ResponseDTO<string> = await this.http.request<SubmitMortgageDealFormDTO, string>(dto);
		if (rs.responseKey == HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			return true;
		}

		return false;
	}

	@action.bound
	public dispose(): void {
		this.mortgageDealForm = {
			loanDetails: {
				borrowCurrency: ONBOARDING_CURRENCY_TYPE.HKD
			},
			securityDetails: {
				purchaseCurrency: ONBOARDING_CURRENCY_TYPE.HKD,
				currentOrExpectedCurrency: ONBOARDING_CURRENCY_TYPE.HKD,
				groundRentPerMonthCurrency: ONBOARDING_CURRENCY_TYPE.HKD,
				buildingManagementFeePerMonthCurrency: ONBOARDING_CURRENCY_TYPE.HKD
			}
		};
		this.guarantors = [];
		this.supportingDocuments = {};
	}

	public async resendEmailGuarantor(guarantor: IGuarantor): Promise<boolean> {
		const dto = new ResendEmailGuarantorDTO(guarantor);

		const rs: ResponseDTO<string> = await this.http.request<ResendEmailGuarantorDTO, string>(dto);

		if (rs.responseKey === HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			return true;
		}

		return false;
	}

	public updateReferralQuestion(value: string): void {
		this.mortgageDealForm.doYouHaveAReferral = value;
	}

	get canSubmitMortgageDealForm(): boolean {
		if (this.guarantors.length === 0) {
			return true;
		}

		if (this.allGuarantorHasConsented) {
			return true;
		}

		return false;
	}

	public updateUseOfFunds(value: string): void {
		this.mortgageDealForm = {
			...this.mortgageDealForm,
			loanDetails: {
				...this.mortgageDealForm.loanDetails,
				useOfFunds: value
			}
		};
	}

	public clearUseOfFundOther(): void {
		this.mortgageDealForm = {
			...this.mortgageDealForm,
			loanDetails: {
				...this.mortgageDealForm.loanDetails,
				useOfFundOther: undefined
			}
		};
	}

	public updateOwnership(value: string): void {
		this.mortgageDealForm = {
			...this.mortgageDealForm,
			securityDetails: {
				...this.mortgageDealForm.securityDetails,
				ownership: value
			}
		};
	}

	public clearOtherOwnership(): void {
		this.mortgageDealForm = {
			...this.mortgageDealForm,
			securityDetails: {
				...this.mortgageDealForm.securityDetails,
				ownershipOther: undefined
			}
		};
	}

	public updateCountrySecurityDetails(value: string): void {
		this.mortgageDealForm = {
			...this.mortgageDealForm,
			securityDetails: {
				...this.mortgageDealForm.securityDetails,
				country: value
			}
		};
	}

	public updateCountrySolicitorDetails(value: string): void {
		this.mortgageDealForm = {
			...this.mortgageDealForm,
			solicitorDetails: {
				...this.mortgageDealForm.solicitorDetails,
				country: value
			}
		};
	}

	public updateCountryPropertyCompanyManagement(value: string): void {
		this.mortgageDealForm = {
			...this.mortgageDealForm,
			propertyCompanyManagement: {
				...this.mortgageDealForm.propertyCompanyManagement,
				country: value
			}
		};
	}

	public updateMortgageApprovedBySecondBankQuestion(value?: boolean): void {
		this.mortgageDealForm = {
			...this.mortgageDealForm,
			securityDetails: {
				...this.mortgageDealForm.securityDetails,
				mortgageApprovedBySecondBankQuestion: value
			}
		};
	}

	public updateIsSecondMortgageForSecurityQuestion(value?: boolean): void {
		this.mortgageDealForm = {
			...this.mortgageDealForm,
			securityDetails: {
				...this.mortgageDealForm.securityDetails,
				isSecondMortgageForSecurityQuestion: value
			}
		};
	}

	public clearMortgageApprovedBySecondBankQuestionNote(): void {
		this.mortgageDealForm = {
			...this.mortgageDealForm,
			securityDetails: {
				...this.mortgageDealForm.securityDetails,
				mortgageApprovedBySecondBankQuestionNote: undefined
			}
		};
	}

	public clearIsSecondMortgageForSecurityQuestionNote(): void {
		this.mortgageDealForm = {
			...this.mortgageDealForm,
			securityDetails: {
				...this.mortgageDealForm.securityDetails,
				isSecondMortgageForSecurityQuestionNote: undefined
			}
		};
	}
}
